import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    InputAdornment,
    IconButton,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material'
import {
    Visibility,
    VisibilityOff,
    Email,
    Lock,
    Language,
} from '@mui/icons-material'
import { useForm } from 'react-hook-form'
import { useAuth } from '@hooks/useAuth'
import { useTranslation } from '@i18n'
import { useUIStore } from '@store/uiStore'
import Loading from '@components/Common/Loading'

const Login = () => {
    const { t, language } = useTranslation()
    const navigate = useNavigate()
    const { login, isLoading, loginError } = useAuth()
    const { themeSettings, setLanguage } = useUIStore()
    const [showPassword, setShowPassword] = useState(false)

    const isRTL = language === 'ar'

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: {
            email: '',
            password: '',
        },
    })

    const onSubmit = async (data) => {
        try {
            await login(data.email, data.password)
        } catch (error) {
            // Error handled in hook
        }
    }

    const handleLanguageChange = (event, newLang) => {
        if (newLang) {
            setLanguage(newLang)
        }
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #1a3a6b 0%, #0d2247 100%)',
                p: 2,
                position: 'relative',
            }}
            dir={isRTL ? 'rtl' : 'ltr'}
        >
            {/* Language Switcher - Top Right Corner */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    left: isRTL ? 20 : 'auto',
                }}
            >
                <ToggleButtonGroup
                    value={language}
                    exclusive
                    onChange={handleLanguageChange}
                    size="small"
                    sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: 2,
                        '& .MuiToggleButton-root': {
                            color: 'white',
                            border: 'none',
                            px: 2,
                            '&.Mui-selected': {
                                bgcolor: 'rgba(255,255,255,0.3)',
                                color: 'white',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.4)',
                                },
                            },
                            '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.2)',
                            },
                        },
                    }}
                >
                    <ToggleButton value="en">EN</ToggleButton>
                    <ToggleButton value="ar">عربي</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    width: '100%',
                    maxWidth: 420,
                    borderRadius: 3,
                }}
            >
                {/* Logo/Header */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Box
                        component="img"
                        src={themeSettings?.logoUrl || '/moj-logo.png'}
                        alt="Logo"
                        sx={{
                            width: 100,
                            height: 'auto',
                            mx: 'auto',
                            mb: 2,
                            filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))',
                        }}
                    />
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                        {t('auth.loginTitle')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('auth.loginSubtitle')}
                    </Typography>
                </Box>

                {/* Error Alert */}
                {loginError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {loginError.response?.data?.message || t('auth.invalidCredentials')}
                    </Alert>
                )}

                {/* Login Form */}
                <form onSubmit={handleSubmit(onSubmit)}>
                    <TextField
                        fullWidth
                        label={t('auth.email')}
                        type="email"
                        margin="normal"
                        {...register('email', {
                            required: 'Email is required',
                            pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: 'Invalid email format',
                            },
                        })}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        InputLabelProps={{
                            sx: {
                                right: isRTL ? 32 : 'auto',
                                left: isRTL ? 'auto' : 14,
                                transformOrigin: isRTL ? 'top right' : 'top left',
                            },
                        }}
                        InputProps={{
                            sx: { textAlign: isRTL ? 'right' : 'left' },
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Email color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        label={t('auth.password')}
                        type={showPassword ? 'text' : 'password'}
                        margin="normal"
                        {...register('password', { required: 'Password is required' })}
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        InputLabelProps={{
                            sx: {
                                right: isRTL ? 32 : 'auto',
                                left: isRTL ? 'auto' : 14,
                                transformOrigin: isRTL ? 'top right' : 'top left',
                            },
                        }}
                        InputProps={{
                            sx: { textAlign: isRTL ? 'right' : 'left' },
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="start"
                                        size="small"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Lock color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={isLoading}
                        sx={{
                            mt: 3,
                            mb: 2,
                            py: 1.5,
                            background: 'linear-gradient(135deg, #1a3a6b 0%, #2a5298 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #0d2247 0%, #1a3a6b 100%)',
                            },
                        }}
                    >
                        {isLoading ? t('auth.loggingIn') : t('auth.loginButton')}
                    </Button>
                </form>
            </Paper>
        </Box>
    )
}

export default Login

